import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Button } from "@mui/material";
import type { GridRowSelectionModel } from "@mui/x-data-grid-premium";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import useInboxStateContext from "../../../../../contexts/useInboxStateContext";
import { markMessageAsRead } from "../../../../../hooks/inboxState/inboxState.api";
import { downloadAll, downloadAllAttachments } from "../../../../../services/attachments";
import { messagesSelector } from "../../../../../store/state/messages/selectors";
import type { AppDispatch } from "../../../../../store/store";

interface Props {
  compact?: boolean;
  selection: GridRowSelectionModel;
}

const DownloadAllAction = ({ compact, selection }: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const messages = useSelector(messagesSelector);
  const { watermarkInvestorName } = useInboxStateContext();
  const { sendNotificationError } = useNotificationContext();

  const [downloading, setDownloading] = useState(false);

  const downloadableMessages = useMemo(
    () => messages.filter((msg) => selection.ids.has(msg.id) && msg.hasAttachments && !msg.isDownloadDisabled),
    [messages, selection]
  );

  if (downloadableMessages.length === 0) {
    return null;
  }

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const [singleMessage] = downloadableMessages;
      if (downloadableMessages.length === 1 && singleMessage) {
        await downloadAllAttachments(singleMessage, watermarkInvestorName);
        if (!singleMessage.isRead) {
          await markMessageAsRead(singleMessage.id)(dispatch);
        }
        return;
      }

      const messageIds = downloadableMessages.map((msg) => msg.id);
      await downloadAll(messageIds, watermarkInvestorName);
      await markMessageAsRead(messageIds)(dispatch);
    } catch (error) {
      logError(error, "[DownloadAllAction] handleDownload");
      sendNotificationError("The attachments cannot be downloaded");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Button
      startIcon={<FileDownloadOutlinedIcon />}
      loadingPosition="start"
      loading={downloading}
      onClick={handleDownload}
      sx={(theme) => ({
        color: theme.palette.text.secondary,
        flexDirection: compact === true ? "column" : "inherit",
      })}
    >
      Download
    </Button>
  );
};

export default DownloadAllAction;
